/**
 * @file TopnavBar.jsx
 * @location src/components/layout/topbar/TopnavBar.jsx
 * @role Topbar 좌측 — 온점 로고(워드마크+점) + nav 링크 (가로 드래그/휠 스크롤 지원)
 *
 * 좁은 폭에서 nav 항목이 잘릴 때 가로 드래그·휠로 스크롤. 드래그 직후 click 1회 차단.
 */
import { useEffect, useRef, useTransition } from 'react';
import { useApp } from '../../../store/AppContext';
import { Icon } from '../../ui/Icon';
import { NAV_ITEMS } from '../../../data/navItems';

/**
 * @param {object} props
 * @param {boolean} props.isManager  매니저 전용 nav(managerOnly) 노출 게이트
 */
export function TopnavBar({ isManager }) {
  const { S, act } = useApp();
  const topnavRef = useRef(null);
  // React 19: lazy 페이지 다운로드 중 이전 페이지 유지 — Suspense fallback 깜빡임 차단.
  // isPending 동안 활성 nav 가 시각적으로 「전환 중」 으로 표시 (CSS .pending).
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const el = topnavRef.current;
    if (!el) return;
    let isDown = false, startX = 0, startScroll = 0, dragged = false;

    const onDown = (e) => {
      if (e.button !== 0) return;
      isDown = true; dragged = false;
      startX = e.pageX; startScroll = el.scrollLeft;
    };
    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (!dragged && Math.abs(dx) <= 4) return;
      dragged = true;
      el.scrollLeft = startScroll - dx;
      e.preventDefault();
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      if (dragged) {
        const blockClick = (ev) => {
          ev.preventDefault(); ev.stopPropagation();
          window.removeEventListener('click', blockClick, true);
        };
        window.addEventListener('click', blockClick, true);
      }
    };
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (e.deltaX !== 0) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  return (
    <div className="tl">
      <div className="logo">
        <span>온점</span><i className="logo-dot" aria-hidden="true" />
      </div>
      <nav className="topnav" ref={topnavRef}>
        {NAV_ITEMS
          .filter(n => !n.managerOnly || isManager)
          .map(n => (
            <button
              key={n.key}
              className={`tnav nav-item${S.activeNav === n.key ? ' on' : ''}${isPending ? ' pending' : ''}`}
              onClick={() => startTransition(() => act('SET_NAV', { nav: n.key }))}
              title={n.label}
            >
              <Icon name={n.icon} size={14} />
              <span className="nav-item-label">{n.label}</span>
            </button>
          ))}
      </nav>
    </div>
  );
}
